import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils";
import { 
  Trophy, Swords, Play, Crown, Medal, RefreshCw, Plus, Zap, Mic
} from "lucide-react";
import { LiveCommentary } from "@/components/dashboard/LiveCommentary";

export function TournamentManager({ competitions, onRefresh }) {
  const { t } = useTranslation();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      const response = await axios.get(`${API}/tournaments`);
      setTournaments(response.data);
    } catch (err) { console.error("TournamentManager:", err.message); } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-bold text-slate-900">Tournament Mode</h2>
          <p className="text-sm text-slate-500">Knockout tournaments for top teams</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-tournament-btn">
              <Plus className="w-4 h-4" />
              Create Tournament
            </Button>
          </DialogTrigger>
          <DialogContent>
            <CreateTournamentDialog 
              competitions={competitions}
              onSuccess={(tournament) => {
                setTournaments([...tournaments, tournament]);
                setCreateOpen(false);
                toast.success("Tournament created!");
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-6">
          {[1, 2].map(i => (
            <div key={i} className="h-64 bg-slate-200 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : tournaments.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Trophy className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Tournaments Yet</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              Create a tournament to pit top teams against each other in exciting knockout rounds!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {tournaments.map(tournament => (
            <TournamentCard 
              key={tournament.id} 
              tournament={tournament}
              onRefresh={fetchTournaments}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TournamentCard({ tournament, onRefresh }) {
  const [expanded, setExpanded] = useState(false);

  const statusColors = {
    active: "bg-emerald-100 text-emerald-700",
    completed: "bg-amber-100 text-amber-700"
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-amber-50 to-amber-100 border-b border-amber-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-200 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-amber-700" />
            </div>
            <div>
              <CardTitle className="font-heading">{tournament.name}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={statusColors[tournament.status]}>
                  {tournament.status === "completed" ? "Completed" : `Round ${tournament.current_round}`}
                </Badge>
                <span className="text-sm text-slate-500">{tournament.bracket_size} teams</span>
              </div>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Hide Bracket" : "View Bracket"}
          </Button>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-6">
          {tournament.brackets?.champion ? (
            <ChampionDisplay champion={tournament.brackets.champion} />
          ) : null}
          
          <TournamentBracket 
            brackets={tournament.brackets} 
            tournamentId={tournament.id}
            currentRound={tournament.current_round}
            onRefresh={onRefresh}
          />
        </CardContent>
      )}
    </Card>
  );
}

function ChampionDisplay({ champion }) {
  return (
    <div className="mb-8 p-6 bg-gradient-to-r from-amber-100 to-amber-50 rounded-xl border-2 border-amber-300 text-center">
      <Crown className="w-12 h-12 text-amber-500 mx-auto mb-3" />
      <h3 className="text-xl font-heading font-bold text-amber-800 mb-1">Tournament Champion</h3>
      <p className="text-2xl font-bold text-amber-900">{champion.team_name}</p>
      <p className="text-amber-600">{champion.airline_code} • {champion.game_name}</p>
    </div>
  );
}

function TournamentBracket({ brackets, tournamentId, currentRound, onRefresh }) {
  if (!brackets?.rounds) return null;

  return (
    <div className="overflow-x-auto">
      <div className="flex gap-8 min-w-max p-4">
        {brackets.rounds.map((round, roundIndex) => (
          <div key={round.round} className="flex flex-col gap-4">
            <h4 className="text-center font-semibold text-slate-700 pb-2 border-b border-slate-200">
              {round.name}
            </h4>
            <div className="flex flex-col gap-4" style={{ justifyContent: 'space-around', minHeight: `${round.matches.length * 150}px` }}>
              {round.matches.map((match, matchIndex) => (
                <MatchCard 
                  key={match.match_id}
                  match={match}
                  tournamentId={tournamentId}
                  isCurrentRound={round.round === currentRound}
                  onRefresh={onRefresh}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MatchCard({ match, tournamentId, isCurrentRound, onRefresh }) {
  const [simulating, setSimulating] = useState(false);
  const [showCommentary, setShowCommentary] = useState(false);

  const handleSimulate = async () => {
    setSimulating(true);
    try {
      await axios.post(`${API}/tournaments/${tournamentId}/simulate-match/${match.match_id}`);
      toast.success("Match simulated!");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to simulate match");
    } finally {
      setSimulating(false);
    }
  };

  const isReady = match.team1 && match.team2;
  const isComplete = match.winner !== null;

  return (
    <div className="space-y-2">
      <div className={`w-64 bg-white border-2 rounded-lg overflow-hidden ${
        isComplete ? 'border-emerald-200' : isReady ? 'border-sky-200' : 'border-slate-200'
      }`}>
        {/* Team 1 */}
        <TeamRow 
          team={match.team1} 
          score={match.score1}
          isWinner={match.winner?.team_id === match.team1?.team_id}
        />
        
        {/* Divider */}
        <div className="flex items-center px-3 py-1 bg-slate-50 border-y border-slate-100">
          <div className="flex-1 h-px bg-slate-200" />
          <Swords className="w-4 h-4 text-slate-400 mx-2" />
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* Team 2 */}
        <TeamRow 
          team={match.team2} 
          score={match.score2}
          isWinner={match.winner?.team_id === match.team2?.team_id}
        />

        {/* Actions */}
        {isCurrentRound && isReady && !isComplete && (
          <div className="p-2 bg-slate-50 border-t border-slate-100 space-y-1">
            <Button 
              size="sm" 
              className="w-full gap-1"
              onClick={handleSimulate}
              disabled={simulating}
            >
              <Zap className="w-3 h-3" />
              {simulating ? "Simulating..." : "Simulate Match"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="w-full gap-1"
              onClick={() => setShowCommentary(!showCommentary)}
              data-testid={`commentary-toggle-${match.match_id}`}
            >
              <Mic className="w-3 h-3" />
              {showCommentary ? "Hide Commentary" : "Live Commentary"}
            </Button>
          </div>
        )}

        {isComplete && isReady && (
          <div className="p-2 bg-slate-50 border-t border-slate-100">
            <Button
              size="sm"
              variant="outline"
              className="w-full gap-1"
              onClick={() => setShowCommentary(!showCommentary)}
              data-testid={`commentary-toggle-${match.match_id}`}
            >
              <Mic className="w-3 h-3" />
              {showCommentary ? "Hide Commentary" : "View Commentary"}
            </Button>
          </div>
        )}
      </div>

      {showCommentary && isReady && (
        <div className="w-96">
          <LiveCommentary tournamentId={tournamentId} match={match} />
        </div>
      )}
    </div>
  );
}

function TeamRow({ team, score, isWinner }) {
  if (!team) {
    return (
      <div className="px-3 py-2 flex items-center gap-2 text-slate-400">
        <span className="text-sm italic">TBD</span>
      </div>
    );
  }

  return (
    <div className={`px-3 py-2 flex items-center gap-2 ${
      isWinner ? 'bg-emerald-50' : ''
    }`}>
      {isWinner && <Medal className="w-4 h-4 text-amber-500" />}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${isWinner ? 'text-emerald-700' : 'text-slate-700'}`}>
          {team.team_name}
        </p>
        <p className="text-xs text-slate-400">{team.airline_code}</p>
      </div>
      {score !== null && score !== undefined && (
        <span className={`text-sm font-mono font-semibold ${isWinner ? 'text-emerald-600' : 'text-slate-500'}`}>
          {score.toFixed(1)}
        </span>
      )}
    </div>
  );
}

function CreateTournamentDialog({ competitions, onSuccess }) {
  const [name, setName] = useState("");
  const [competitionId, setCompetitionId] = useState("");
  const [bracketSize, setBracketSize] = useState("8");
  const [creating, setCreating] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!competitionId) {
      toast.error("Please select a competition");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/tournaments`, {
        name,
        competition_id: competitionId,
        bracket_size: parseInt(bracketSize)
      });
      onSuccess(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create tournament");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          Create Tournament
        </DialogTitle>
        <DialogDescription>
          Top teams from the competition will be seeded into knockout brackets
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={handleCreate} className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label htmlFor="tournamentName">Tournament Name</Label>
          <Input
            id="tournamentName"
            placeholder="MBA Championship Finals"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            data-testid="tournament-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label>Competition</Label>
          <Select value={competitionId} onValueChange={setCompetitionId}>
            <SelectTrigger data-testid="tournament-competition-select">
              <SelectValue placeholder="Select a competition" />
            </SelectTrigger>
            <SelectContent>
              {competitions?.map(comp => (
                <SelectItem key={comp.id} value={comp.id}>
                  {comp.name} ({comp.games?.length || 0} games)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Bracket Size</Label>
          <Select value={bracketSize} onValueChange={setBracketSize}>
            <SelectTrigger data-testid="bracket-size-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="4">4 teams (2 rounds)</SelectItem>
              <SelectItem value="8">8 teams (3 rounds)</SelectItem>
              <SelectItem value="16">16 teams (4 rounds)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500">Top performing teams will be automatically seeded</p>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="submit" disabled={creating} data-testid="create-tournament-submit">
            {creating ? "Creating..." : "Create Tournament"}
          </Button>
        </div>
      </form>
    </>
  );
}

export default TournamentManager;
